/**
 * Main entry point for the App view
 */

import { getStoreValue, updateStoreValue } from './store';
import App from './components/App.svelte';
import ChromeBrowserExtensionLoginFlow from './components/ChromeBrowserExtensionLoginFlow.svelte';

document.addEventListener('DOMContentLoaded', init);

/**
 * DOM dependent functionality should be initialised here.
 * Do not put code in here that does not depend on the DOM.
 */
function init() {
    initializeStore()
        .then(() => {
            initializeView();
        });
}

function initializeStore() {
    updateStoreValue(Object.assign({}, window.proxyStore));

    return new Promise((resolve) => {
        resolveSsoSource()
            .then((result) => {
                updateStoreValue({ ssoLoginSource: result });
                resolve();
            })
            .catch(() => {
                // Unknown or forbidden ssoLoginSource, redirect to welcome-page.
                const url = new URL(getStoreValue().settings.itslearningLoginUrl);

                window.location = url.href;
            });
    });
}

function initializeView() {
    const target = document.getElementById('gsuitelogin-app-hook');

    const { ssoLoginSource, ssoLoginSources } = getStoreValue();

    updateStoreValue({ clientId: window.settings.CLIENT_ID });

    if (ssoLoginSource === ssoLoginSources.chromeBrowserExtension) {
        // eslint-disable-next-line
        new ChromeBrowserExtensionLoginFlow({ target });
    } else {
        // eslint-disable-next-line
        new App({ target });
    }
}

const resolveSsoSource = function () {
    const { settings, ssoLoginSources, redirectLocationType } = getStoreValue();

    return new Promise((resolve, reject) =>
        checkIfChromebook(settings.chromebookExtensionId)
            .then((result) => (result ? resolve(ssoLoginSources.chromebook) : false))
            .then(() => checkIfGSuiteMarketplace(settings.gSuiteMarketplaceAppReferrer)
                .then((result) => (result ? resolve(ssoLoginSources.gSuite) : false))
                .then(() => checkIfChromeBrowserExtension()
                    .then((result) => (result ? resolve(ssoLoginSources.chromeBrowserExtension) : false))
                    .then(() => checkIfGoogleCalendar(settings.itslearningLocationTypes, redirectLocationType)
                        .then((result) => (result ? resolve(ssoLoginSources.googleCalendar) : reject(Error(ssoLoginSources.browser))))
                    )
                )
            )
    );
};

const checkIfChromebook = function (extensionId) {
    return new Promise(function (resolve) {
        if (window.chrome && window.chrome.runtime) {
            try {
                window.chrome.runtime.sendMessage(extensionId, { message: 'isChromebook' },
                    function (response) {
                        if (response && response.isChromebook) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
            } catch (e) {
                resolve(false);
            }
        } else {
            resolve(false);
        }
    });
};

const checkIfGSuiteMarketplace = function (gSuiteMarketplaceAppReferrer) {
    return new Promise(function (resolve) {
        // Check if the referrer includes marketplaceApp
        const referrer = document.referrer;
        const isMarketplaceApp = referrer.indexOf(gSuiteMarketplaceAppReferrer) > -1;

        if (isMarketplaceApp) {
            resolve(true);
        } else {
            resolve(false);
        }
    });
};

const checkIfChromeBrowserExtension = function () {
    return new Promise(function (resolve) {
        const { settings } = getStoreValue();

        if (document.location.ancestorOrigins && document.location.ancestorOrigins[0] === 'chrome-extension://' + settings.chromeBrowserExtensionAppStoreId) {
            resolve(true);
        } else {
            resolve(false);
        }
    });
};

const checkIfGoogleCalendar = function (itslearningLocationTypes, redirectLocationType) {
    return new Promise(function (resolve) {
        if (typeof redirectLocationType !== 'undefined' &&
            typeof itslearningLocationTypes !== 'undefined' &&
            redirectLocationType &&
            itslearningLocationTypes) {
            if (itslearningLocationTypes.indexOf(redirectLocationType) > -1) {
                resolve(true);
            } else {
                resolve(false);
            }
        } else {
            resolve(false);
        }
    });
};
